// The Sales Order packaging plan's pallet and box rules: ONE copy, used by the desktop Sales Order
// page (pages/SalesOrderPage.jsx: SOPackagingPlanCard, SOPalletActionBar, SOPlannedBoxCard) and
// the phone order screen (mobile/screens/SalesOrderDetailScreen.jsx). docs/mobile-pwa-plan.md
// "One copy of every action". Pure: no requests.
import { assemblyCountFromPrimarySpace } from '../../utils/assemblyMath';

/** A pallet's stable name: PLT-XXXX, or P# on rows from before April 2026 that have no short_id. */
export function palletDisplayName(plan, palletId) {
  const pal = (plan?.pallets || []).find((p) => p.pallet_id === palletId);
  return pal?.short_id || `P${pal?.pallet_num ?? '?'}`;
}

const hasPacked = (box) => (box.zones || []).some((z) => (z.actual_qty || 0) > 0);

/** Whether Seal pallet may be pressed. A pallet with nothing packed can't be sealed, and every real
 *  box with parts in it must be sealed first (Seal pallet is the finisher: it prints the pallet and
 *  stack labels only). The API auto-seals a full box anyway, but the screen asks for box seals. */
export function palletSealState(pallet) {
  const boxes = pallet?.boxes || [];
  const anyPacked = boxes.some(hasPacked);
  const realBoxes = boxes.filter((b) => !b.is_pallet_direct);
  const unsealedBoxesWithContent = realBoxes.filter((b) => hasPacked(b) && b.status !== 'Sealed');
  return {
    anyPacked,
    realBoxes,
    unsealedBoxesWithContent,
    gatedBySealBoxes: unsealedBoxesWithContent.length > 0,
  };
}

/** A box's own actions. Seal: a real box (not a pallet-direct stack) on a Building pallet that is
 *  full, by count or by the yield policy (effective_status from load_plan). Reprint its label: a
 *  sealed real box with its shipment record. */
export function boxActionState(box, { palletStatus, palletShipmentGroupId }) {
  const isDirect = !!box?.is_pallet_direct;
  const effStatus = box?.effective_status || box?.status;
  return {
    canSeal: !isDirect && palletStatus === 'Building' && (effStatus === 'Full' || effStatus === 'FullPerPolicy'),
    canReprintLabel: !isDirect && box?.status === 'Sealed' && !!box?.shipment_box_id && !!palletShipmentGroupId,
  };
}

/** A laser task's WIP pallet (jobq-api 2026-10-27, `planned_pallets.wip_task_id`): it holds the order's share of
 *  that task's parts that still owe an in-house step. It never seals, ships or stages; it empties as the office packs
 *  the parts from the Staging queue. Old WIP pallets (is_wip, no task) are not task WIP pallets. */
export function isTaskWipPallet(pallet) {
  return pallet?.wip_task_id != null;
}

/** The station a task WIP pallet's label and beacon read STAGE FOR (the API walks it: Finish -> Brake -> Packing). */
export function taskWipStation(pallet) {
  return pallet?.wip_station || pallet?.wip_next_station || 'Packing';
}

/** "Holding pallet · then Finish" (jobq-api holding pallets, 2026-10-27: every part goes on a holding pallet at
 *  the laser, and the task WIP pallet IS that holding pallet). '' for any other pallet. */
export function holdingPalletText(pallet) {
  if (!isTaskWipPallet(pallet)) return '';
  return `Holding pallet · then ${taskWipStation(pallet)}`;
}

/** "Holding pallet · then Finish · Laser task 40965 · Job 10321" (the holding pallet's header badge). */
export function taskWipHeader(pallet) {
  if (!isTaskWipPallet(pallet)) return '';
  const bits = [holdingPalletText(pallet), `Laser task ${pallet.wip_task_id}`];
  if (pallet.wip_master_job_num != null) bits.push(`Job ${pallet.wip_master_job_num}`);
  return bits.join(' · ');
}

/** The Staging queue row's WIP line, one per WIP pallet: "19 on PLT-K2Q8 (laser task 40965, job 10321) → Finish".
 *  Sheet by sheet (jobq-api 2026-10-26): `qty` = cut and on the WIP pallet, `reserved` = claimed, still to cut
 *  ("19 on PLT-K2Q8, 33 still being cut (…)"; nothing cut yet: "52 being cut for PLT-K2Q8 (…)"). Only pieces ON
 *  the pallet can be packed. */
export function onWipText(w) {
  const where = [`laser task ${w.task_id}`];
  if (w.master_job_num != null) where.push(`job ${w.master_job_num}`);
  const pallet = w.short_id || `pallet ${w.pallet_id}`;
  const qty = Number(w.qty) || 0;
  const reserved = Number(w.reserved) || 0;
  let head;
  if (qty <= 0 && reserved > 0) head = `${reserved} being cut for ${pallet}`;
  else head = reserved > 0 ? `${qty} on ${pallet}, ${reserved} still being cut` : `${qty} on ${pallet}`;
  return `${head} (${where.join(', ')}) → ${w.next_station || 'Packing'}`;
}

/** What manual-assign's answer says about the WIP pallets it took pieces off (null when it took none):
 *  how many off which pallet, a pallet left empty (merged into the destination, its beacon freed), and the
 *  API's warnings when the job still owes the part an open step (packing then is allowed). */
export function wipDrainNote(res) {
  const w = res?.wip_drained;
  if (!w || !(w.drained || []).length) return null;
  const by = {};
  for (const d of w.drained) {
    const name = d.short_id || `pallet ${d.pallet_id}`;
    by[name] = (by[name] || 0) + d.qty;
  }
  const took = Object.entries(by).map(([pal, q]) => `${q} off ${pal}`).join(', ');
  const parts = [`Took ${took} (laser task WIP).`];
  for (const e of w.ended || []) {
    parts.push(`${e.short_id || `Pallet ${e.pallet_id}`} is empty now and was merged into the destination.`);
  }
  if ((w.beacons_released || []).length) parts.push(`Beacon ${w.beacons_released.join(', ')} freed: take it off.`);
  for (const x of res.wip_warnings || []) parts.push(x);
  return parts.join(' ');
}

/** What a phone offers on a pallet, in order. The desktop's action bar draws the same states with
 *  more controls (move, unseal, part labels, container type), which stay desktop-only. A laser task's WIP
 *  pallet offers no Seal (the API refuses it: its parts are packed from the Staging queue). */
export function palletPhoneActions(pallet, fulfillmentType) {
  const status = pallet?.status || 'Building';
  const isPickup = fulfillmentType === 'pickup';
  const seal = palletSealState(pallet);
  const actions = [];
  if (status === 'Building' && !isTaskWipPallet(pallet)) {
    actions.push({
      key: 'seal',
      enabled: seal.anyPacked && (pallet.is_wip || !seal.gatedBySealBoxes),
      why: !seal.anyPacked
        ? 'Nothing packed on this pallet yet.'
        : (!pallet.is_wip && seal.gatedBySealBoxes ? `Seal the ${seal.unsealedBoxesWithContent.length} box(es) with parts first.` : null),
    });
  } else if (status === 'Ready') {
    actions.push({ key: isPickup ? 'stage' : 'ship', enabled: true });
    actions.push({ key: 'reprintLabels', enabled: !!pallet.shipment_group_id });
  } else if (status === 'Staged') {
    actions.push({ key: 'confirmPickup', enabled: true });
    actions.push({ key: 'reprintLabels', enabled: !!pallet.shipment_group_id });
    actions.push({ key: 'manifest', enabled: !!pallet.shipment_group_id });
  } else if (status === 'Shipped') {
    actions.push({ key: 'reprintLabels', enabled: !!pallet.shipment_group_id });
    actions.push({ key: 'manifest', enabled: !!pallet.shipment_group_id });
  }
  if (status !== 'Consolidated') actions.push({ key: 'statusLabel', enabled: true });
  return actions;
}

/** Pallets a proof-of-delivery record can cover: shipped, or staged for pickup. */
export function podEligiblePallets(pallets) {
  return (pallets || []).filter((p) => p.status === 'Shipped' || p.status === 'Staged');
}

/** A box's name: its BOX-XXXX (printed on its label, used on every manifest and slip), else the
 *  pre-April-2026 ordinal (B# for a carton, S# for a loose stack). */
export function boxLabel(box) {
  const n = box?.display_box_num ?? box?.box_num;
  return box?.short_id || (box?.is_pallet_direct ? `S${n}` : `B${n}`);
}

/** A box's contents by part (zones of one part summed; by part id, as the desktop always listed them). An assembly zone counts in
 *  primary-part space, so displayActual / displayPlanned are whole assemblies ("25 of Hy-Flo Stand",
 *  not "100 of HYFLO-LEG"); a part shows its own count. */
export function boxContents(box) {
  const roll = {};
  for (const z of box?.zones || []) {
    const key = z.part_id;
    if (!roll[key]) {
      roll[key] = {
        part_id: z.part_id, product_id: z.product_id, description: z.description,
        assembly_id: z.assembly_id, assembly_name: z.assembly_name,
        primary_part_quantity: z.primary_part_quantity || 1,
        planned: 0, actual: 0, zones: 0,
      };
    }
    roll[key].planned += z.planned_qty;
    roll[key].actual += z.actual_qty || 0;
    roll[key].zones += 1;
  }
  return Object.values(roll).map((r) => {
    const isAssembly = !!r.assembly_id && !!r.assembly_name;
    const ppq = Math.max(1, parseInt(r.primary_part_quantity, 10) || 1);
    return {
      ...r,
      isAssembly,
      ppq,
      displayActual: isAssembly ? assemblyCountFromPrimarySpace(r.actual, ppq) : r.actual,
      displayPlanned: isAssembly ? assemblyCountFromPrimarySpace(r.planned, ppq) : r.planned,
    };
  });
}

/** A pallet at a glance (the phone package guide): pieces packed on it (zone actual qty, in part pieces as the desktop
 *  box cards count them) of how many planned, its cartons and loose stacks, and its beacon (null when none). */
export function palletSummary(pallet) {
  const boxes = pallet?.boxes || [];
  let pieces = 0;
  let planned = 0;
  for (const b of boxes) {
    for (const z of b.zones || []) {
      pieces += Number(z.actual_qty) || 0;
      planned += Number(z.planned_qty) || 0;
    }
  }
  return {
    pieces,
    planned,
    boxes: boxes.filter((b) => !b.is_pallet_direct).length,
    stacks: boxes.filter((b) => b.is_pallet_direct).length,
    beacon: pallet?.beacon_id || null,
    beaconLit: !!pallet?.beacon_light,
  };
}

/** "24 of 24 pcs · 2 boxes · 1 stack" (the summary's words). */
export function palletSummaryText(s) {
  const bits = [`${s.pieces} of ${s.planned} pcs`];
  if (s.boxes) bits.push(`${s.boxes} box${s.boxes === 1 ? '' : 'es'}`);
  if (s.stacks) bits.push(`${s.stacks} stack${s.stacks === 1 ? '' : 's'}`);
  return bits.join(' · ');
}
